import React from 'react';
import { Box, ButtonBase, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { EDD_BUCKET_LABEL, EDD_OWNERS, EDD_OWNER_KEYS } from './constants';

// Table header cells, as the Admin Panel (sharedStyles.tableHeadSx).
const tableHeadSx = { fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', bgcolor: 'grey.200' };

/**
 * Bucket × attribution counts for the active tab (Admin Panel pages/ptl-edd-breach/components/AgeingMatrix.js).
 * Clicking a non-zero cell sets the bucket + attribution filters (the "All" row clears the bucket, the Total column
 * clears the attribution). The selected cell is highlighted.
 */
export default function AgeingMatrix({ tab, matrix, loading, selected, onPick }) {
  const undelivered = tab === 'U';
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{undelivered ? 'Ageing against attribution' : 'Lateness against attribution'}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
        {undelivered
          ? 'How long each undelivered breach has been sitting past EDD, against who the delay is on. This clock is still running. Click any number to filter the list.'
          : 'How late each delivered shipment landed against EDD, against who the delay is on. This clock stopped at delivery. Click any number to filter the list.'}
      </Typography>
      {loading || !matrix ? <Skeleton variant='rectangular' height={190} /> : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeadSx}>{undelivered ? 'Days past EDD' : 'Days late at delivery'}</TableCell>
              {EDD_OWNER_KEYS.map((o) => <TableCell key={o} align='right' sx={tableHeadSx}>{EDD_OWNERS[o].short}</TableCell>)}
              <TableCell align='right' sx={tableHeadSx}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {matrix.map((row) => {
              const bucket = row.bucket === 'All' ? '' : row.bucket;
              const cells = [...EDD_OWNER_KEYS.map((o) => ({ o, n: row.counts?.[o] || 0 })), { o: '', n: row.total || 0 }];
              return (
                <TableRow key={row.bucket} sx={row.bucket === 'All' ? { '& td': { fontWeight: 700, borderTop: '2px solid #DFE3E8' } } : undefined}>
                  <TableCell sx={{ fontSize: 12 }}>{EDD_BUCKET_LABEL[row.bucket] || row.bucket}</TableCell>
                  {cells.map(({ o, n }) => {
                    const on = selected && selected.bucket === bucket && selected.owner === o && (bucket || o);
                    return (
                      <TableCell key={o || 'total'} align='right' sx={{ p: 0.5 }}>
                        <ButtonBase
                          disabled={!n}
                          onClick={() => onPick({ bucket, owner: o })}
                          aria-label={`${n} shipments, ${o ? EDD_OWNERS[o].short : 'all attributions'}, ${EDD_BUCKET_LABEL[row.bucket]}`}
                          sx={{
                            minWidth: 40, px: 1, py: 0.5, borderRadius: 1, fontSize: 13, fontWeight: n ? 700 : 400,
                            fontVariantNumeric: 'tabular-nums',
                            color: n ? (o ? EDD_OWNERS[o].color : 'text.primary') : 'text.disabled',
                            bgcolor: on ? '#D1E9FC' : 'transparent',
                            outline: on ? '2px solid #2065D1' : 'none',
                            '&:hover': { bgcolor: n ? '#F4F6F8' : 'transparent' },
                          }}
                        >
                          {n}
                        </ButtonBase>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
